import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress, MenuItem, Stack, Step, StepLabel, Stepper,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { AlertTriangle, ArrowLeft, ArrowRight, BookOpen, CheckCircle2, DatabaseZap, Download, FileUp, FolderKanban, RotateCcw, Trash2, Users } from "lucide-react";
import { GlassCard, PageHeader, T, TYPE, labelSx, primaryButtonSx, ghostButtonSx } from "../user/complaints/shared";
import { pm, errorMessage, saveBlob } from "./api";
import { PmPage, usePm } from "./PmContext";
import { fieldSx } from "./inputs";
import * as XLSX from "xlsx";
import { readExport, autoMap, templateCsv, TEMPLATE_COLUMNS } from "./importCsv";

// Data import wizard: a CSV/Excel file (our template, or a full-field export
// from another issue tracker) becomes workspace projects and issues with their
// original keys, people, statuses, dates, comments, parents and links.

const STEPS = ["Upload export", "Projects", "Map values", "Review", "Import"];
const CHUNK = 100;
const KEY_RE = /^[A-Z][A-Z0-9]{1,9}$/;

const Section = ({ title, hint, children }) => (
  <GlassCard sx={{ p: 2.5, mb: 2 }}>
    <Typography sx={{ fontSize: TYPE.cardTitle, fontWeight: 700 }}>{title}</Typography>
    {hint && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mt: 0.5, mb: 1.5 }}>{hint}</Typography>}
    {children}
  </GlassCard>
);

/** Imported value -> workspace option, one row each, with how many issues use it. */
const NEW_PERSON = "__new__";

const MappingTable = ({ label, values, counts, map, onChange, options, emptyLabel, onAddNew }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={labelSx}>In your file: {label}</TableCell>
        <TableCell sx={labelSx} align="right">Issues</TableCell>
        <TableCell sx={labelSx}>Becomes</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {values.map((v) => (
        <TableRow key={v}>
          <TableCell sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{v}</TableCell>
          <TableCell align="right" sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{counts[v] || 0}</TableCell>
          <TableCell sx={{ width: "45%" }}>
            <TextField select size="small" fullWidth value={map[v] || ""} onChange={(e) => (e.target.value === NEW_PERSON ? onAddNew(v) : onChange(v, e.target.value))} sx={fieldSx}
              error={!emptyLabel && !map[v]}>
              {emptyLabel && <MenuItem value=""><em>{emptyLabel}</em></MenuItem>}
              {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              {onAddNew && <MenuItem value={NEW_PERSON} sx={{ color: "#4F46E5", fontWeight: 600 }}>+ Add as new team member…</MenuItem>}
            </TextField>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const a = new Uint32Array(10);
  crypto.getRandomValues(a);
  return Array.from(a, (n) => chars[n % chars.length]).join("");
};

/** Create a team member for someone in the import file who is not in the workspace yet. */
const AddMemberDialog = ({ person, roles, onClose, onCreated }) => {
  const isEmail = EMAIL_RE.test(person || "");
  const [form, setForm] = useState({ name: isEmail ? "" : person || "", email: isEmail ? person : "", password: genPassword(), sendInvite: true, roleId: String((roles.find((r) => r.key === "ws_member") || roles[0] || {})._id || "") });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim() && EMAIL_RE.test(form.email.trim()) && form.password.length >= 6;
  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      onCreated(await pm.invitePerson({ ...form, name: form.name.trim(), email: form.email.trim() }));
    } catch (e) {
      setErr(errorMessage(e, "Could not add team member"));
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Add team member</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField size="small" label="Name" value={form.name} onChange={set("name")} sx={fieldSx} />
          <TextField size="small" label="Email" type="email" value={form.email} onChange={set("email")} sx={fieldSx} />
          {roles.length > 0 && (
            <TextField select size="small" label="Project role" value={form.roleId} onChange={set("roleId")} sx={fieldSx}
              helperText="Controls what this person can do in project management.">
              {roles.map((r) => <MenuItem key={String(r._id)} value={String(r._id)}>{r.name}</MenuItem>)}
            </TextField>
          )}
          <TextField size="small" label="Password" value={form.password} onChange={set("password")} sx={fieldSx}
            helperText="At least 6 characters. Sent to the person if you email the invitation."
            InputProps={{ endAdornment: <Button size="small" onClick={() => setForm({ ...form, password: genPassword() })}>Generate</Button> }} />
          <FormControlLabel control={<Checkbox checked={form.sendInvite} onChange={(e) => setForm({ ...form, sendInvite: e.target.checked })} />}
            label={<Typography sx={{ fontSize: TYPE.small }}>Email the login details to this address</Typography>} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={ghostButtonSx}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!valid || busy} sx={primaryButtonSx}>{busy ? "Adding…" : "Add & match"}</Button>
      </DialogActions>
    </Dialog>
  );
};

const downloadTemplate = () => saveBlob(new Blob(["\uFEFF" + templateCsv()], { type: "text/csv;charset=utf-8" }), "issue-import-template.csv");

/** Template download, column reference and preparation tips. */
const ImportGuide = () => {
  const [open, setOpen] = useState(false);
  return (
    <Section title="Template & guide" hint="Moving from a spreadsheet? Start from the template. Moving from another tool? Check how your export's columns are read.">
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
        <Button variant="contained" sx={primaryButtonSx} startIcon={<Download size={15} />} onClick={downloadTemplate}>Download template (CSV)</Button>
        <Button variant="outlined" sx={ghostButtonSx} startIcon={<BookOpen size={15} />} onClick={() => setOpen(!open)}>{open ? "Hide column guide" : "Show column guide"}</Button>
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: open ? 2 : 0 }}>
        {[
          ["1. Prepare", "Download the template (opens in Excel or Google Sheets), or export issues from your current tool with all fields."],
          ["2. Fill in", "One row per issue. Link sub-tasks to their parent with \"Parent key\". Keep the header row as it is."],
          ["3. Save", "Save as CSV (UTF-8) or Excel (.xlsx). Only the first sheet is read."],
          ["4. Upload & map", "Upload here, map statuses, types and people, validate, then import. Re-running skips issues already imported."],
        ].map(([t, d]) => (
          <Box key={t} sx={{ p: 1.5, bgcolor: T.surfaceMuted, borderRadius: T.radiusTile, border: `1px solid ${T.border}` }}>
            <Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{t}</Typography>
            <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{d}</Typography>
          </Box>
        ))}
      </Box>
      {open && (
        <>
          <Box sx={{ overflowX: "auto", border: `1px solid ${T.border}`, borderRadius: T.radiusTile }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={labelSx}>Column</TableCell>
                  <TableCell sx={labelSx}>Example</TableCell>
                  <TableCell sx={labelSx}>How it is used</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {TEMPLATE_COLUMNS.map((c) => (
                  <TableRow key={c.name}>
                    <TableCell sx={{ fontSize: TYPE.small, fontWeight: 600, whiteSpace: "nowrap" }}>
                      {c.name}{c.required && <Chip size="small" label="Required" sx={{ ml: 1, height: 18, fontSize: TYPE.micro, bgcolor: T.brandSoft, color: T.brand }} />}
                    </TableCell>
                    <TableCell sx={{ fontSize: TYPE.small, fontFamily: "monospace", color: T.inkSecondary }}>{c.example}</TableCell>
                    <TableCell sx={{ fontSize: TYPE.small, color: T.inkSecondary, minWidth: 260 }}>{c.note}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Stack spacing={0.5} sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }}>Tips</Typography>
            {[
              "Add your team members in the CRM before importing, so assignees and comment authors are matched instead of left unassigned.",
              "A parent that is referenced but missing from the file is created automatically (named from \"Parent summary\" when present).",
              "Exports from other tools usually work as they are: extra columns are ignored and repeated Comment / Labels columns are read.",
              "Nobody is notified while importing, so a large migration does not flood inboxes.",
              "Try a few rows first to check the mapping, then import the rest. Issues already imported are skipped.",
            ].map((t) => <Typography key={t} sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>• {t}</Typography>)}
          </Stack>
        </>
      )}
    </Section>
  );
};

const Stat = ({ label, value, tone = T.ink }) => (
  <Box sx={{ minWidth: 110 }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ fontSize: TYPE.statValue, fontWeight: 700, color: tone }}>{value}</Typography>
  </Box>
);

const Problems = ({ results }) => {
  const bad = results.filter((r) => r.status === "error");
  const warn = results.filter((r) => r.status !== "error" && r.warnings?.length && r.status !== "skipped");
  if (!bad.length && !warn.length) return null;
  return (
    <Box sx={{ maxHeight: 320, overflow: "auto", border: `1px solid ${T.border}`, borderRadius: T.radiusTile, mt: 2 }}>
      <Table size="small" stickyHeader>
        <TableHead><TableRow><TableCell sx={labelSx}>Issue key</TableCell><TableCell sx={labelSx}>Problem</TableCell></TableRow></TableHead>
        <TableBody>
          {[...bad, ...warn].map((r) => (
            <TableRow key={r.ref}>
              <TableCell sx={{ fontSize: TYPE.small, fontWeight: 600, whiteSpace: "nowrap" }}>{r.ref}</TableCell>
              <TableCell sx={{ fontSize: TYPE.small, color: r.status === "error" ? "#B42318" : "#B54708" }}>
                {r.status === "error" ? `Will be skipped: ${r.errors.join("; ")}` : r.warnings.join("; ")}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

/** Two-step, typed confirmation before every project, board and issue is permanently deleted. */
const ResetDialog = ({ onClose, onDone }) => {
  const [summary, setSummary] = useState(null);
  const [stage, setStage] = useState(1);
  const [first, setFirst] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    pm.resetSummary().then(setSummary).catch((e) => setErr(errorMessage(e, "Could not load what will be deleted")));
  }, []);

  const total = summary?.items.reduce((n, i) => n + i.count, 0) || 0;
  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      await pm.resetWorkspace(phrase.trim());
      onDone();
    } catch (e) {
      setErr(errorMessage(e, "Reset failed"));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#B42318", display: "flex", alignItems: "center", gap: 1 }}>
        <AlertTriangle size={18} /> {stage === 1 ? "Reset all project management data" : "Final confirmation"}
      </DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}
        {!summary && !err && <LinearProgress sx={{ borderRadius: 4 }} />}
        {summary && stage === 1 && (
          <Stack spacing={1.5}>
            <Alert severity="error">This permanently deletes the data below for the whole workspace. It cannot be undone and cannot be recovered — not from trash and not by support.</Alert>
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.5 }}>Will be deleted ({total.toLocaleString()} records)</Typography>
              <Table size="small">
                <TableBody>
                  {summary.items.map((i) => (
                    <TableRow key={i.key}>
                      <TableCell sx={{ fontSize: TYPE.small, py: 0.5 }}>{i.label}</TableCell>
                      <TableCell align="right" sx={{ fontSize: TYPE.small, py: 0.5, fontWeight: 700, color: i.count ? "#B42318" : T.inkMuted }}>{i.count.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.5 }}>Will be kept</Typography>
              {summary.kept.map((k) => <Typography key={k} sx={{ fontSize: TYPE.small, color: "#027A48" }}>✓ {k}</Typography>)}
            </Box>
            <TextField size="small" label='Type "RESET" to continue' value={first} onChange={(e) => setFirst(e.target.value)} sx={fieldSx} autoComplete="off" />
          </Stack>
        )}
        {summary && stage === 2 && (
          <Stack spacing={1.5}>
            <Typography sx={{ fontSize: TYPE.body }}>
              You are about to permanently delete <strong>{summary.items.find((i) => i.key === "projects")?.count || 0} projects</strong>,{" "}
              <strong>{summary.items.find((i) => i.key === "boards")?.count || 0} boards</strong> and{" "}
              <strong>{summary.items.find((i) => i.key === "issues")?.count || 0} issues</strong>, with all their comments, files and history.
              There is no way to get them back.
            </Typography>
            <TextField size="small" label={`Type "${summary.confirm}" to confirm`} value={phrase} onChange={(e) => setPhrase(e.target.value)} sx={fieldSx} autoComplete="off"
              helperText="Must match exactly, in capitals." />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={ghostButtonSx}>Cancel</Button>
        {stage === 1 ? (
          <Button variant="contained" color="error" disabled={!summary || first.trim() !== "RESET"} onClick={() => setStage(2)}>Continue</Button>
        ) : (
          <Button variant="contained" color="error" startIcon={<Trash2 size={15} />} disabled={busy || phrase.trim() !== summary.confirm} onClick={submit}>
            {busy ? "Deleting…" : "Permanently delete everything"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const DataImportInner = () => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [workspaceProjects, setWorkspaceProjects] = useState([]);
  const [targets, setTargets] = useState({});
  const [maps, setMaps] = useState({ types: {}, statuses: {}, priorities: {}, people: {} });
  const [options, setOptions] = useState({ keepKeys: true, comments: true, preserveDates: true, links: true });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [review, setReview] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [addFor, setAddFor] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);

  const onResetDone = async () => {
    setResetOpen(false);
    setWorkspaceProjects([]);
    await ctx.reload();
    ctx.notify("All project management data was permanently deleted");
  };

  useEffect(() => {
    pm.projects().then((r) => setWorkspaceProjects(r || [])).catch(() => {});
  }, []);

  const canCreateProjects = ctx.canCreateProject;
  const templateFor = (p) => (/kanban/i.test(p.name) ? ctx.templates.find((t) => t.key === "kanban") : null) || ctx.templates.find((t) => t.key === "software") || ctx.templates[0];

  const onFile = async (file) => {
    setError("");
    try {
      const text = /\.xlsx?$/i.test(file.name)
        ? (({ Sheets, SheetNames }) => XLSX.utils.sheet_to_csv(Sheets[SheetNames[0]]))(XLSX.read(await file.arrayBuffer(), { type: "array", raw: true }))
        : await file.text();
      const parsed = readExport(text);
      setFileName(file.name);
      setData(parsed);
      setMaps(autoMap(parsed, ctx));
      setTargets(Object.fromEntries(Object.values(parsed.projects).map((p) => {
        const same = workspaceProjects.find((w) => w.key === p.key);
        return [p.key, same || !canCreateProjects
          ? { mode: "existing", projectId: same?._id || workspaceProjects[0]?._id || "" }
          : { mode: "new", key: p.key, name: p.name, templateId: templateFor(p)?._id || "", visibility: "private" }];
      })));
      setReview(null);
      setOutcome(null);
      setStep(1);
    } catch (err) {
      setError(err.message || "Could not read the file");
    }
  };

  const counts = useMemo(() => {
    const c = { types: {}, statuses: {}, priorities: {}, people: {} };
    for (const i of data?.issues || []) {
      for (const [k, v] of [["types", i.type], ["statuses", i.status], ["priorities", i.priority], ["people", i.assignee?.name], ["people", i.reporter?.name]]) if (v) c[k][v] = (c[k][v] || 0) + 1;
    }
    return c;
  }, [data]);

  const setMap = (kind) => (value, id) => {
    setMaps((m) => ({ ...m, [kind]: { ...m[kind], [value]: id } }));
    setReview(null);
  };
  const setTarget = (key, patch) => {
    setTargets((t) => ({ ...t, [key]: { ...t[key], ...patch } }));
    setReview(null);
  };

  const projectsValid = Object.values(targets).every((t) => (t.mode === "existing" ? t.projectId : KEY_RE.test(String(t.key || "").toUpperCase()) && t.name && t.templateId));
  const mapsValid = Object.values(maps.types).every(Boolean) && Object.values(maps.statuses).every(Boolean);

  // parents before children across chunks: epics, then standard issues, then sub-tasks
  const ordered = useMemo(() => {
    if (!data) return [];
    const level = (i) => ctx.typeById.get(String(maps.types[i.type]))?.level ?? 1;
    return [...data.issues].sort((a, b) => level(a) - level(b));
  }, [data, maps.types, ctx]);
  const index = useMemo(() => Object.fromEntries((data?.issues || []).map((i) => [i.key, { type: i.type, projectKey: i.projectKey }])), [data]);
  const linkList = options.links ? data?.links || [] : [];

  const payload = (issues, projects, dryRun, links = []) => ({
    dryRun, issues, links, index, maps, accounts: data.accounts, options,
    projects: Object.fromEntries(Object.entries(projects).map(([k, t]) => [k, t.mode === "existing" ? { mode: "existing", projectId: t.projectId } : { mode: "new", key: String(t.key).toUpperCase(), name: t.name, templateId: t.templateId, visibility: t.visibility }])),
  });

  const runAll = async (dryRun) => {
    setBusy(true);
    setError("");
    setProgress(0);
    const sum = { created: 0, valid: 0, skipped: 0, failed: 0, comments: 0, links: 0, linkErrors: [], results: [], projects: {} };
    let projects = { ...targets };
    try {
      const chunks = [];
      for (let i = 0; i < ordered.length; i += CHUNK) chunks.push(ordered.slice(i, i + CHUNK));
      if (linkList.length) chunks.push([]);
      for (let c = 0; c < chunks.length; c += 1) {
        const isLast = c === chunks.length - 1;
        const r = await pm.importData(payload(chunks[c], projects, dryRun, isLast ? linkList : []));
        for (const k of ["created", "valid", "skipped", "failed", "comments", "links"]) sum[k] += r[k] || 0;
        sum.linkErrors.push(...(r.linkErrors || []));
        sum.results.push(...r.results.filter(Boolean));
        Object.assign(sum.projects, r.projects);
        // a project created by the first chunk receives the rest of its issues
        if (!dryRun) for (const [k, p] of Object.entries(r.projects || {})) projects = { ...projects, [k]: { mode: "existing", projectId: p.projectId } };
        setProgress(Math.round(((c + 1) / chunks.length) * 100));
      }
      if (dryRun) setReview(sum);
      else {
        setTargets(projects);
        setOutcome(sum);
        ctx.notify(`${sum.created} issues imported`);
      }
    } catch (err) {
      setError(errorMessage(err, dryRun ? "Validation failed" : "Import stopped — run it again to continue; issues already imported are skipped"));
      if (!dryRun) {
        setTargets(projects);
        setOutcome({ ...sum, interrupted: true });
      }
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(0);
    setData(null);
    setReview(null);
    setOutcome(null);
    setError("");
  };

  const typeOptions = ctx.issueTypes.filter((t) => t.isActive !== false).map((t) => ({ value: String(t._id), label: `${t.name}${t.level === 0 ? " (epic)" : t.level === 2 ? " (sub-task)" : ""}` }));
  const statusOptions = ctx.statuses.map((s) => ({ value: String(s._id), label: `${s.name} · ${s.category.replace("_", " ")}` }));
  const priorityOptions = ctx.priorities.map((p) => ({ value: String(p._id), label: p.name }));
  const onMemberCreated = async (person) => {
    const who = addFor;
    setAddFor(null);
    await ctx.reload();
    setMap("people")(who, String(person.id));
    ctx.notify(person.emailed ? "Team member added and invitation emailed" : "Team member added");
  };
  const peopleOptions = ctx.people.map((p) => ({ value: String(p.id), label: `${p.name}${p.email ? ` · ${p.email}` : ""}` }));
  const unmatchedPeople = Object.values(maps.people).filter((v) => !v).length;
  const commentTotal = data?.issues.reduce((n, i) => n + i.comments.length, 0) || 0;

  return (
    <>
      <PageHeader icon={DatabaseZap} title="Import Data"
        subtitle="Bring projects, issues, people, comments and history from your previous tool or a spreadsheet into this workspace"
        actions={<Button variant="outlined" sx={ghostButtonSx} startIcon={<ArrowLeft size={15} />} onClick={() => navigate("/projects")}>Projects</Button>} />

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
        {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {step === 0 && (
        <>
        <Section title="Upload your file"
          hint="CSV or Excel: our template, or an issue export from your previous tool (choose the option that includes all fields). Large workspaces can be imported project by project.">
          <Box sx={{ textAlign: "center", py: 5, border: `2px dashed ${T.borderStrong}`, borderRadius: T.radiusTile, bgcolor: T.surfaceMuted }}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}>
            <FileUp size={30} color={T.brand} />
            <Typography sx={{ fontSize: TYPE.body, fontWeight: 600, mt: 1 }}>Drop your CSV or Excel file here</Typography>
            <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mb: 2 }}>or</Typography>
            <Button variant="contained" component="label" sx={primaryButtonSx}>
              Choose file<input hidden type="file" accept=".csv,text/csv,.xlsx,.xls" onChange={(e) => { if (e.target.files[0]) onFile(e.target.files[0]); e.target.value = ""; }} />
            </Button>
          </Box>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2.5 }}>
            {[
              ["Issues & hierarchy", "Epics, stories, tasks, bugs and sub-tasks with their parents. Parents missing from the export are recreated."],
              ["People & history", "Assignees, reporters and comment authors matched to your team; created, updated and resolved dates kept."],
              ["Safe to re-run", "Everything is validated first. Running the import again skips issues that were already brought over."],
            ].map(([t, d]) => (
              <Box key={t} sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{t}</Typography>
                <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{d}</Typography>
              </Box>
            ))}
          </Stack>
        </Section>
        <ImportGuide />
        {ctx.me?.isAdmin && (
          <GlassCard sx={{ p: 2.5, mb: 2, border: "1px solid #FDA29B" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
              <Box>
                <Typography sx={{ fontSize: TYPE.cardTitle, fontWeight: 700, color: "#B42318" }}>Danger zone: reset project data</Typography>
                <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mt: 0.5 }}>
                  Permanently delete every project, board, issue and everything in them, e.g. to start a clean import. Users and settings are kept. This cannot be undone.
                </Typography>
              </Box>
              <Button variant="outlined" color="error" startIcon={<Trash2 size={15} />} onClick={() => setResetOpen(true)} sx={{ flexShrink: 0 }}>Reset all project data</Button>
            </Stack>
          </GlassCard>
        )}
        {resetOpen && <ResetDialog onClose={() => setResetOpen(false)} onDone={onResetDone} />}
        </>
      )}

      {step >= 1 && data && step < 4 && (
        <Alert severity="info" icon={<FileUp size={18} />} sx={{ mb: 2 }}
          action={<Button size="small" startIcon={<RotateCcw size={14} />} onClick={reset}>Change file</Button>}>
          <strong>{fileName}</strong> — {data.issues.length - data.placeholders} issues{data.placeholders ? ` + ${data.placeholders} parent issues referenced but not in the file (they will be recreated from their summary)` : ""}, {commentTotal} comments, {data.links.length} links, {Object.keys(data.projects).length} project(s).
        </Alert>
      )}

      {step === 1 && data && (
        <Section title="Where should each project go?" hint="Create a new project (recommended — keeps the same project key, so PRJ-42 stays PRJ-42) or add the issues to a project you already have.">
          {Object.values(data.projects).map((p) => {
            const t = targets[p.key] || {};
            return (
              <Box key={p.key} sx={{ border: `1px solid ${T.border}`, borderRadius: T.radiusTile, p: 2, mb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <FolderKanban size={17} color={T.brand} />
                  <Typography sx={{ fontWeight: 700, fontSize: TYPE.body }}>{p.name}</Typography>
                  <Chip size="small" label={p.key} />
                  <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{p.count} issues</Typography>
                </Stack>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                  <TextField select size="small" label="Import into" value={t.mode || "new"} sx={{ ...fieldSx, minWidth: 200 }}
                    onChange={(e) => setTarget(p.key, e.target.value === "new" ? { mode: "new", key: p.key, name: p.name, templateId: templateFor(p)?._id || "", visibility: "private" } : { mode: "existing", projectId: workspaceProjects.find((w) => w.key === p.key)?._id || workspaceProjects[0]?._id || "" })}>
                    <MenuItem value="new" disabled={!canCreateProjects}>New project</MenuItem>
                    <MenuItem value="existing" disabled={!workspaceProjects.length}>Existing project</MenuItem>
                  </TextField>
                  {t.mode === "existing" ? (
                    <TextField select size="small" label="Project" value={t.projectId || ""} onChange={(e) => setTarget(p.key, { projectId: e.target.value })} sx={{ ...fieldSx, minWidth: 280 }}>
                      {workspaceProjects.map((w) => <MenuItem key={w._id} value={w._id}>{w.key} · {w.name}</MenuItem>)}
                    </TextField>
                  ) : (
                    <>
                      <TextField size="small" label="Name" value={t.name || ""} onChange={(e) => setTarget(p.key, { name: e.target.value })} sx={{ ...fieldSx, flex: 1 }} />
                      <TextField size="small" label="Key" value={t.key || ""} onChange={(e) => setTarget(p.key, { key: e.target.value.toUpperCase() })} sx={{ ...fieldSx, width: 120 }}
                        error={!KEY_RE.test(String(t.key || "")) || workspaceProjects.some((w) => w.key === t.key)}
                        helperText={workspaceProjects.some((w) => w.key === t.key) ? "Already used" : ""} />
                      <TextField select size="small" label="Template" value={t.templateId || ""} onChange={(e) => setTarget(p.key, { templateId: e.target.value })} sx={{ ...fieldSx, minWidth: 170 }}>
                        {ctx.templates.map((tp) => <MenuItem key={tp._id} value={tp._id}>{tp.name}</MenuItem>)}
                      </TextField>
                      <TextField select size="small" label="Visibility" value={t.visibility || "private"} onChange={(e) => setTarget(p.key, { visibility: e.target.value })} sx={{ ...fieldSx, minWidth: 140 }}>
                        {["private", "team", "shared"].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </>
                  )}
                </Stack>
              </Box>
            );
          })}
          {!canCreateProjects && <Alert severity="warning">You cannot create projects, so issues go into an existing project. Ask a workspace admin for the “Create projects” permission to import into new ones.</Alert>}
        </Section>
      )}

      {step === 2 && data && (
        <>
          <Section title="Issue types" hint="Sub-tasks need a sub-task type; their parents must be a standard type (task, story, bug).">
            <MappingTable label="type" values={Object.keys(maps.types)} counts={counts.types} map={maps.types} onChange={setMap("types")} options={typeOptions} />
          </Section>
          <Section title="Statuses" hint="If a project's workflow does not contain the chosen status, the first status of the same category (To do / In progress / Done) is used.">
            <MappingTable label="status" values={Object.keys(maps.statuses)} counts={counts.statuses} map={maps.statuses} onChange={setMap("statuses")} options={statusOptions} />
          </Section>
          {Object.keys(maps.priorities).length > 0 && (
            <Section title="Priorities">
              <MappingTable label="priority" values={Object.keys(maps.priorities)} counts={counts.priorities} map={maps.priorities} onChange={setMap("priorities")} options={priorityOptions} emptyLabel="Workspace default" />
            </Section>
          )}
          <Section title="People" hint="Matched by name or email. People you leave unmatched are imported as unassigned (their original name is kept on the issue); add them as team members first if they should own work here.">
            {unmatchedPeople > 0 && <Alert severity="warning" icon={<Users size={18} />} sx={{ mb: 1.5 }}>{unmatchedPeople} {unmatchedPeople === 1 ? "person has" : "people have"} no match in your team.</Alert>}
            <MappingTable label="person" values={Object.keys(maps.people)} counts={counts.people} map={maps.people} onChange={setMap("people")} options={peopleOptions} emptyLabel="Not in team — leave unassigned" onAddNew={ctx.me?.isAdmin ? setAddFor : undefined} />
            {addFor !== null && <AddMemberDialog person={addFor} roles={ctx.workspaceRoles} onClose={() => setAddFor(null)} onCreated={onMemberCreated} />}
          </Section>
        </>
      )}

      {step === 3 && data && (
        <Section title="Review and validate" hint="Validation checks every issue against its target project without saving anything.">
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <FormControlLabel control={<Checkbox checked={options.keepKeys} onChange={(e) => { setOptions({ ...options, keepKeys: e.target.checked }); setReview(null); }} />}
              label={<Typography sx={{ fontSize: TYPE.small }}>Keep original issue numbers (PRJ-42 stays PRJ-42) when the project key is the same</Typography>} />
            <FormControlLabel control={<Checkbox checked={options.preserveDates} onChange={(e) => { setOptions({ ...options, preserveDates: e.target.checked }); setReview(null); }} />}
              label={<Typography sx={{ fontSize: TYPE.small }}>Keep original created, updated and resolved dates</Typography>} />
            <FormControlLabel control={<Checkbox checked={options.comments} onChange={(e) => { setOptions({ ...options, comments: e.target.checked }); setReview(null); }} />}
              label={<Typography sx={{ fontSize: TYPE.small }}>Import comments ({commentTotal})</Typography>} />
            {data.links.length > 0 && <FormControlLabel control={<Checkbox checked={options.links} onChange={(e) => { setOptions({ ...options, links: e.target.checked }); setReview(null); }} />}
              label={<Typography sx={{ fontSize: TYPE.small }}>Import issue links ({data.links.length})</Typography>} />}
          </Stack>
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mb: 2 }}>Nobody is notified during the import. Attachment links in the file are added to the issue description (the files themselves are not copied). Sprints are kept as labels.</Typography>
          {busy && <LinearProgress variant="determinate" value={progress} sx={{ mb: 2, borderRadius: 4 }} />}
          {review && (
            <>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Stat label="Ready" value={review.valid} tone="#027A48" />
                <Stat label="Already imported" value={review.skipped} />
                <Stat label="Will be skipped" value={review.failed} tone={review.failed ? "#B42318" : T.ink} />
                <Stat label="Comments" value={review.comments} />
                {linkList.length > 0 && <Stat label="Links" value={review.links} />}
              </Stack>
              <Alert severity={review.failed ? "warning" : "success"} sx={{ mt: 2 }}>
                {review.failed ? `${review.failed} issues have problems and will be skipped — fix the mapping above or continue without them.` : "Everything checks out. Ready to import."}
              </Alert>
              <Problems results={review.results} />
            </>
          )}
        </Section>
      )}

      {step === 4 && (
        <Section title={busy ? "Importing…" : outcome?.interrupted ? "Import interrupted" : "Import complete"}>
          {busy && (
            <>
              <LinearProgress variant="determinate" value={progress} sx={{ mb: 1, borderRadius: 4 }} />
              <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{progress}% — keep this page open.</Typography>
            </>
          )}
          {outcome && !busy && (
            <>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Stat label="Imported" value={outcome.created} tone="#027A48" />
                <Stat label="Already there" value={outcome.skipped} />
                <Stat label="Skipped" value={outcome.failed} tone={outcome.failed ? "#B42318" : T.ink} />
                <Stat label="Comments" value={outcome.comments} />
                {linkList.length > 0 && <Stat label="Links" value={outcome.links} />}
              </Stack>
              {!outcome.interrupted && <Alert severity="success" icon={<CheckCircle2 size={18} />} sx={{ mb: 2 }}>Your data is now in the workspace.</Alert>}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.values(outcome.projects).filter((p) => p.key).map((p) => (
                  <Button key={p.key} variant="contained" sx={primaryButtonSx} startIcon={<FolderKanban size={15} />} onClick={() => navigate(`/projects/${p.key}/issues`)}>Open {p.key}</Button>
                ))}
                {outcome.interrupted && <Button variant="outlined" sx={ghostButtonSx} onClick={() => runAll(false)}>Resume import</Button>}
                <Button variant="outlined" sx={ghostButtonSx} onClick={reset}>Import another file</Button>
              </Stack>
              <Problems results={outcome.results} />
              {outcome.linkErrors.length > 0 && <Typography sx={{ fontSize: TYPE.micro, color: "#B54708", mt: 1 }}>{outcome.linkErrors.length} links could not be created: {outcome.linkErrors.slice(0, 5).join("; ")}</Typography>}
            </>
          )}
        </Section>
      )}

      {step >= 1 && step <= 3 && (
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
          <Button variant="outlined" sx={ghostButtonSx} startIcon={<ArrowLeft size={15} />} disabled={busy} onClick={() => setStep(step - 1)}>Back</Button>
          {step < 3 && (
            <Button variant="contained" sx={primaryButtonSx} endIcon={<ArrowRight size={15} />} disabled={(step === 1 && !projectsValid) || (step === 2 && !mapsValid)} onClick={() => setStep(step + 1)}>Next</Button>
          )}
          {step === 3 && (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" sx={ghostButtonSx} disabled={busy} onClick={() => runAll(true)}>{busy ? "Validating…" : review ? "Validate again" : "Validate"}</Button>
              <Button variant="contained" sx={primaryButtonSx} disabled={busy || !review || !(review.valid + review.skipped)}
                onClick={() => { setStep(4); runAll(false); }}>Import {review ? review.valid : ""} issues</Button>
            </Stack>
          )}
        </Stack>
      )}
    </>
  );
};

const DataImport = () => (
  <PmPage maxWidth="lg">
    <DataImportInner />
  </PmPage>
);

export default DataImport;
